import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import './SharedInstructions.css';

const LANGUAGES = [
  { code: 'en', name: 'English', speechCode: 'en-IN' },
  { code: 'hi', name: 'हिंदी (Hindi)', speechCode: 'hi-IN' },
  { code: 'ta', name: 'தமிழ் (Tamil)', speechCode: 'ta-IN' },
  { code: 'te', name: 'తెలుగు (Telugu)', speechCode: 'te-IN' },
  { code: 'kn', name: 'ಕನ್ನಡ (Kannada)', speechCode: 'kn-IN' },
  { code: 'ml', name: 'മലയാളം (Malayalam)', speechCode: 'ml-IN' },
  { code: 'mr', name: 'मराठी (Marathi)', speechCode: 'mr-IN' },
  { code: 'bn', name: 'বাংলা (Bengali)', speechCode: 'bn-IN' },
  { code: 'gu', name: 'ગુજરાતી (Gujarati)', speechCode: 'gu-IN' },
  { code: 'pa', name: 'ਪੰਜਾਬੀ (Punjabi)', speechCode: 'pa-IN' },
];

const SharedInstructions = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [selectedLang, setSelectedLang] = useState(searchParams.get('lang') || 'en');
  const [translatedText, setTranslatedText] = useState('');
  const [isTranslating, setIsTranslating] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  useEffect(() => {
    fetchInstructions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedLang]);

  const fetchInstructions = async () => {
    try {
      setLoading(true);
      // Backend route is at /share/instructions/:token (not under /api)
      const BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
      const response = await fetch(`${BASE_URL}/share/instructions/${token}?lang=${selectedLang}`);
      const result = await response.json();

      if (!result.success) {
        setError(result.message || 'Failed to load instructions');
        return;
      }

      setData(result.data);

      // Set text based on available translations
      if (result.data.instructions.voiceNote) {
        const voiceNote = result.data.instructions.voiceNote;
        const sourceLang = voiceNote.sourceLang || 'en';
        const targetLang = voiceNote.targetLang;

        // If user already translated and rider's language matches target language, use it
        if (voiceNote.translatedText && targetLang === selectedLang) {
          setTranslatedText(voiceNote.translatedText);
        }
        // If rider selected the source language, show original
        else if (sourceLang === selectedLang) {
          setTranslatedText(voiceNote.originalText);
        }
        // Otherwise, translate to rider's selected language
        else if (voiceNote.originalText) {
          translateText(voiceNote.originalText, sourceLang, selectedLang);
        }
      }
    } catch (err) {
      setError('Failed to load instructions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const translateText = async (text, sourceLang, targetLang) => {
    if (!text || sourceLang === targetLang) {
      setTranslatedText(text);
      return;
    }

    setIsTranslating(true);
    try {
      // Use server-side translation API (Google Translate)
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_URL}/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text,
          sourceLang,
          targetLang
        })
      });

      const data = await response.json();
      console.log('Translation response:', data);

      if (data.success && data.translatedText) {
        setTranslatedText(data.translatedText);
      } else {
        setTranslatedText(data.translatedText || text);
      }
    } catch (err) {
      console.error('Translation error:', err);
      setTranslatedText(text);
    } finally {
      setIsTranslating(false);
    }
  };

  // Use server-side TTS for Indian languages
  const speakText = async (text) => {
    if (!text) return;

    // Stop any ongoing speech
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }

    setIsSpeaking(true);

    // For Indian languages, use server-side TTS
    const indianLanguages = ['hi', 'ta', 'te', 'kn', 'ml', 'mr', 'bn', 'gu', 'pa'];
    if (indianLanguages.includes(selectedLang)) {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        console.log('Fetching TTS for:', selectedLang, 'text:', text.substring(0, 50));

        const response = await fetch(`${API_URL}/tts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text, lang: selectedLang })
        });

        console.log('TTS response status:', response.status);

        if (response.ok) {
          const audioBlob = await response.blob();
          console.log('Audio blob size:', audioBlob.size, 'type:', audioBlob.type);

          const audioUrl = URL.createObjectURL(audioBlob);
          const audio = new Audio(audioUrl);

          audio.oncanplaythrough = () => console.log('Audio ready to play');
          audio.onended = () => {
            console.log('Audio ended');
            setIsSpeaking(false);
            URL.revokeObjectURL(audioUrl);
          };
          audio.onerror = (e) => {
            console.error('Audio error:', e, audio.error);
            setIsSpeaking(false);
            URL.revokeObjectURL(audioUrl);
          };

          try {
            await audio.play();
            console.log('Audio play started');
          } catch (playError) {
            console.error('Audio play failed:', playError);
            setIsSpeaking(false);
          }
          return;
        } else {
          console.error('TTS response not ok:', response.status);
        }
      } catch (err) {
        console.error('Server TTS failed:', err);
      }
    }

    // Fallback to browser TTS for English
    if (window.speechSynthesis) {
      const utterance = new SpeechSynthesisUtterance(text);
      const langConfig = LANGUAGES.find(l => l.code === selectedLang);
      utterance.lang = langConfig?.speechCode || 'en-IN';
      utterance.rate = 0.9;

      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => setIsSpeaking(false);
      utterance.onerror = () => setIsSpeaking(false);

      window.speechSynthesis.speak(utterance);
    } else {
      setIsSpeaking(false);
    }
  };

  const stopSpeaking = () => {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    setIsSpeaking(false);
  };

  const handleLanguageChange = (e) => {
    const newLang = e.target.value;
    setSelectedLang(newLang);

    if (data?.instructions.voiceNote) {
      const voiceNote = data.instructions.voiceNote;
      const sourceLang = voiceNote.sourceLang || 'en';
      const targetLang = voiceNote.targetLang;

      // Use pre-translated text if rider selects the target language
      if (voiceNote.translatedText && targetLang === newLang) {
        setTranslatedText(voiceNote.translatedText);
      }
      // Show original if rider selects source language
      else if (sourceLang === newLang) {
        setTranslatedText(voiceNote.originalText);
      }
      // Otherwise translate on-the-fly
      else if (voiceNote.originalText) {
        translateText(voiceNote.originalText, sourceLang, newLang);
      }
    }
  };

  if (loading) {
    return (
      <div className="shared-instructions-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading delivery instructions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="shared-instructions-page">
        <div className="error-container">
          <span className="error-icon">⚠️</span>
          <h2>Oops!</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="shared-instructions-page">
      <header className="shared-header">
        <div className="brand">
          <span className="brand-icon">🚀</span>
          <span className="brand-text">DeliveryBridge</span>
        </div>
      </header>

      <main className="shared-content">
        <div className="welcome-card">
          <h1>Delivery Instructions</h1>
          <p className="customer-name">From: <strong>{data.customerName}</strong></p>
          {data.orderId && <p className="order-id">Order: #{data.orderId}</p>}
        </div>

        {/* Language Selector */}
        <div className="language-selector-card">
          <label>
            <span className="label-icon">🌐</span>
            Select Your Language / अपनी भाषा चुनें
          </label>
          <select value={selectedLang} onChange={handleLanguageChange}>
            {LANGUAGES.map(lang => (
              <option key={lang.code} value={lang.code}>{lang.name}</option>
            ))}
          </select>
        </div>

        {/* Voice Instructions */}
        {data.instructions.voiceNote?.originalText && (
          <div className="instructions-card voice-card">
            <h2><span className="card-icon">🎤</span> Voice Instructions</h2>

            <div className="voice-content">
              {isTranslating ? (
                <div className="translating">
                  <div className="mini-spinner"></div>
                  <span>Translating...</span>
                </div>
              ) : (
                <>
                  <p className="translated-text">{translatedText}</p>

                  <div className="voice-actions">
                    {isSpeaking ? (
                      <button className="btn-stop" onClick={stopSpeaking}>
                        <span>⏹️</span> Stop
                      </button>
                    ) : (
                      <button className="btn-listen" onClick={() => speakText(translatedText)}>
                        <span>🔊</span> Listen in {LANGUAGES.find(l => l.code === selectedLang)?.name}
                      </button>
                    )}
                  </div>

                  {data.instructions.voiceNote.sourceLang !== selectedLang && (
                    <p className="original-note">
                      <small>Original ({LANGUAGES.find(l => l.code === data.instructions.voiceNote.sourceLang)?.name}): {data.instructions.voiceNote.originalText}</small>
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Text Instructions */}
        {data.instructions.textInstructions && (
          <div className="instructions-card text-card">
            <h2><span className="card-icon">📝</span> Written Instructions</h2>
            <p>{data.instructions.textInstructions}</p>
          </div>
        )}

        {/* Photos */}
        {data.instructions.photos && data.instructions.photos.length > 0 && (
          <div className="instructions-card photos-card">
            <h2><span className="card-icon">📷</span> Location Photos</h2>
            <div className="photos-grid">
              {data.instructions.photos.map((photo, index) => (
                <div
                  key={index}
                  className="photo-item"
                  onClick={() => setSelectedPhoto(photo)}
                >
                  <img src={photo.src} alt={photo.label || 'Location'} />
                  <div className="photo-label">{photo.label}</div>
                  {photo.caption && <p className="photo-caption">{photo.caption}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Photo Modal */}
        {selectedPhoto && (
          <div className="photo-modal" onClick={() => setSelectedPhoto(null)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <button className="close-btn" onClick={() => setSelectedPhoto(null)}>×</button>
              <img src={selectedPhoto.src} alt={selectedPhoto.label} />
              <div className="modal-info">
                <h3>{selectedPhoto.label}</h3>
                {selectedPhoto.caption && <p>{selectedPhoto.caption}</p>}
              </div>
            </div>
          </div>
        )}

        {/* Help Section */}
        <div className="help-card">
          <h3>Need Help?</h3>
          <p>If you're having trouble finding the location, the customer has provided the above instructions to help you.</p>
          <p className="tip">💡 Tip: Use the "Listen" button to hear instructions in your preferred language!</p>
        </div>
      </main>

      <footer className="shared-footer">
        <p>Powered by DeliveryBridge</p>
      </footer>
    </div>
  );
};

export default SharedInstructions;
